import React, { useCallback, useEffect } from "react";

import { uniq } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import CheckboxLabel from "@busbud/design-system-components/dist/CheckboxLabel";
import Rating from "@busbud/design-system-components/dist/Rating";
import Skeleton from "@busbud/design-system-components/dist/Skeleton";
import Tooltip from "@busbud/design-system-components/dist/Tooltip";

import { createStyles } from "@app/components/tss/hooks/use-tss-styles";
import { useAppContext } from "@app/helpers/hooks";
import { getSelectedOperatorIds } from "@app/modules/results/selectors/get-selected-operator-ids";
import { getSelectedProfileIds } from "@app/modules/results/selectors/get-selected-profile-ids";
import { getTripsOperatorsProfileNames } from "@app/modules/results/selectors/get-trips-operators-profile-names";
import {
  getOperatorProfileRatings,
  getTripsOperators
} from "@app/modules/results/selectors/state-accessors";
import {
  setOperators,
  setOperatorProfiles
} from "@app/modules/results/state/filters";

import type { FilterCollapseProps } from "../filter-collapse";

import { FilterCollapse } from "../filter-collapse";
import { useFacetedSearch } from "../hooks/useFacetedSearch";

type Props = Pick<FilterCollapseProps, "className">;

const useStyles = createStyles({
  ratingsWrapper: {
    display: "flex", // Prevents extra space caused by components inline display
    flexShrink: 0
  }
});

export const OperatorFilter = ({ className }: Props) => {
  const dispatch = useDispatch();
  const { translator } = useAppContext();
  const { classes } = useStyles();

  const faceted_search = useFacetedSearch();
  const aggregation = faceted_search.getAggregation("profile_ids");
  const trips_operators_profile_names_map = useSelector(
    getTripsOperatorsProfileNames
  );
  const trips_operators = useSelector(getTripsOperators);
  const state_filter_operators_value = useSelector(getSelectedOperatorIds);
  const state_filter_profiles_value = useSelector(getSelectedProfileIds);
  const operator_profile_ratings = useSelector(getOperatorProfileRatings);

  // TODO (use-profile-ids): monitor for `operator_ids` query param for ~2 weeks, then remove if there are no requests.
  useEffect(() => {
    if (state_filter_operators_value.length) {
      const initial_filtered_profiles = state_filter_operators_value
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        .map(operator_id => trips_operators[operator_id].profile_id)
        .filter(id => !!id) as number[];

      dispatch(
        setOperatorProfiles(
          uniq([...state_filter_profiles_value, ...initial_filtered_profiles])
        )
      );
      dispatch(setOperators([]));
    }
  }, [
    dispatch,
    state_filter_operators_value,
    state_filter_profiles_value,
    trips_operators
  ]);

  const getCheckboxValue = useCallback(
    (key: string | number) => state_filter_profiles_value.includes(Number(key)),
    [state_filter_profiles_value]
  );

  function handleCheckboxChange(key: number) {
    return state_filter_profiles_value.includes(key)
      ? dispatch(
          setOperatorProfiles(
            state_filter_profiles_value.filter(id => id !== key)
          )
        )
      : dispatch(setOperatorProfiles([...state_filter_profiles_value, key]));
  }

  const getRatingsLabel = useCallback(
    (ratings: number) => {
      return !!ratings
        ? translator.t("!results.filters.operator-ratings", { ratings })
        : translator.t("!results.filters.no-operator-ratings");
    },
    [translator]
  );

  const bucketsData = React.useMemo(() => {
    return aggregation?.buckets.map(({ key, doc_count }) => ({
      key,
      disabled: doc_count <= 0,
      value: getCheckboxValue(key),
      label: `${trips_operators_profile_names_map.get(Number(key))} (${doc_count})`,
      ratings: operator_profile_ratings[key]
    }));
  }, [
    getCheckboxValue,
    trips_operators_profile_names_map,
    aggregation?.buckets,
    operator_profile_ratings
  ]);

  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  if (!bucketsData.length || bucketsData.length === 1) {
    return null;
  }

  return (
    <FilterCollapse
      title={translator.t("!results.filters.operators")}
      className={className}
    >
      {bucketsData?.map(({ key, disabled, value, label, ratings }) => (
        <div key={key} className="flex items-center justify-between">
          <CheckboxLabel
            outlined
            shape="square"
            disabled={disabled}
            label={label}
            value={value}
            checked={value}
            onChange={() => handleCheckboxChange(Number(key))}
          />
          {/* operators ratings are fetched independently from inventory so display a skeleton if not ready */}
          {/*
           // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591 */}
          {ratings?.global_rating >= 0 ? (
            // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            <Tooltip arrow title={getRatingsLabel(ratings.count_total)}>
              {/*
                Additional div wrapper to make the tooltip work, because of the `pointer-events: none` on the read-only `Rating`,
                TODO: [dscl-override], remove after `Rating` fix https://github.com/busbud/design-system/issues/1184
              */}
              <div className={classes.ratingsWrapper}>
                <Rating
                  size="sm"
                  readOnly
                  precision={0.5}
                  variant="outlined"
                  disabled={disabled}
                  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                  value={ratings.global_rating}
                />
              </div>
            </Tooltip>
          ) : (
            <Skeleton width={9} />
          )}
        </div>
      ))}
    </FilterCollapse>
  );
};
